import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useEffect, useRef} from "react";
import {invalidateRenewalConsumers, renewalQueryKind, knownRenewalRevision, numericRenewalRevision} from './renewalCacheUtils';
import {isRenewalUpdatingError} from './renewalResultUtils';
import apiClient from "./apiClient";
import {config} from "../config";

export const DATA_STATUS_QUERY_KEY_ROOT = 'data-status';
export const DATA_STATUS_CACHE_TIME = 1000 * 60 * 60 * 36;
export const ONBOARDING_STATUS_POLL_INTERVAL = 8000;
const DATA_STATUS_MAX_RETRIES = 2;

export const getDataStatusQueryKey = (userId, activeAccountId) => [
    DATA_STATUS_QUERY_KEY_ROOT,
    userId,
    activeAccountId,
];

export const fetchDataStatus = async (requestConfig = {}) => {
    const response = await apiClient.get(
        `${config.REACT_APP_BACKEND_HOST}/configure/data/status`,
        requestConfig
    );
    return response.data;
};

export const invalidateDataStatusQuery = (queryClient, userId, activeAccountId) => {
    if (!queryClient) {
        return Promise.resolve();
    }

    const queryKey = userId && activeAccountId
        ? getDataStatusQueryKey(userId, activeAccountId)
        : userId
            ? [DATA_STATUS_QUERY_KEY_ROOT, userId]
            : [DATA_STATUS_QUERY_KEY_ROOT];

    return queryClient.invalidateQueries({queryKey});
};

export const shouldRetryDataStatusQuery = (failureCount, error) => {
    const status = error?.response?.status;

    if ([401, 403, 410, 429].includes(status) || error?.isGone || error?.isRateLimit) {
        return false;
    }

    return failureCount < DATA_STATUS_MAX_RETRIES;
};

export const dataStatusRefetchInterval = query => {
    const processingStatus = query?.state?.data?.dataStatus?.processing_status_new;
    const hasRunningBuild = Array.isArray(processingStatus) && processingStatus.some(entry => {
        const status = entry?.status || {};
        return typeof entry?.statusEntry === 'string'
            && entry.statusEntry.startsWith('root||')
            && Boolean(status.started)
            && !status.completed;
    });

    return hasRunningBuild ? ONBOARDING_STATUS_POLL_INTERVAL : false;
};

const renewalStatusRefetchInterval = (query, renewalPending) => {
    const metadata = query?.state?.data?.dataStatus?.renewal_metadata;
    const building = metadata ? metadata.build_in_progress : dataStatusRefetchInterval(query);
    const waiting = metadata && metadata.forecast_enabled !== false && !metadata?.generation_id && !metadata?.build_failed;
    return building || waiting || renewalPending() ? ONBOARDING_STATUS_POLL_INTERVAL : 30000;
};

export const getDataStatusQueryOptions = ({onboardingActive = false, renewalActive = false, renewalPending = () => false} = {}) => ({
    staleTime: onboardingActive ? 0 : renewalActive ? 30000 : DATA_STATUS_CACHE_TIME,
    gcTime: DATA_STATUS_CACHE_TIME,
    refetchOnMount: onboardingActive || renewalActive ? 'always' : false,
    refetchOnWindowFocus: renewalActive ? 'always' : true,
    refetchOnReconnect: renewalActive ? 'always' : true,
    refetchInterval: onboardingActive ? ONBOARDING_STATUS_POLL_INTERVAL : renewalActive
        ? query => renewalStatusRefetchInterval(query, renewalPending) : dataStatusRefetchInterval,
    refetchIntervalInBackground: !renewalActive || onboardingActive,
});

export const useFetchDataStatus = (
    isAuthenticated,
    userId,
    activeAccountId,
    options = {},
) => {
    const queryClient = useQueryClient();
    const observed = useRef(null);
    const queryOptions = getDataStatusQueryOptions({...options, renewalPending: () => queryClient.getQueryCache().getAll()
        .some(query => renewalQueryKind(query.queryKey, activeAccountId) && isRenewalUpdatingError(query.state.error))});
    const query = useQuery({
        queryKey: getDataStatusQueryKey(userId, activeAccountId),
        queryFn: async ({signal}) => {
            const response = await fetchDataStatus({signal, headers: {'X-QFlow-Account-Id': activeAccountId}});
            const metadata = response?.dataStatus?.renewal_metadata;
            if (metadata?.customer_id === activeAccountId) {
                const revision = Math.max(numericRenewalRevision(metadata.matching_revision), knownRenewalRevision(queryClient, activeAccountId));
                // Unmigrated accounts use an opaque legacy hash, not a counter.
                if (revision >= 0) metadata.matching_revision = revision;
            }
            return response;
        },
        ...queryOptions,
        enabled: isAuthenticated && !!userId && !!activeAccountId,
        retry: shouldRetryDataStatusQuery,
    });
    const metadata = query.data?.dataStatus?.renewal_metadata;
    const streamingUpdate = query.data?.dataStatus?.last_streaming_update;
    const version = JSON.stringify(metadata && [metadata.generation_id, metadata.matching_revision, metadata.completed_build, streamingUpdate]);
    useEffect(() => {
        if (!isAuthenticated || !options.renewalActive || metadata?.customer_id !== activeAccountId) return;
        const key = `${activeAccountId}:${version}`;
        if (observed.current?.key === key) return;
        const previous = observed.current;
        observed.current = {key, accountId: activeAccountId, streamingUpdate, completedBuild: metadata.completed_build};
        // Mount/focus already fetch stale queries. Do not cancel an initial load
        // a second time just because its first status response arrived.
        if (!previous || previous.accountId !== activeAccountId) return;
        const streamed = previous.streamingUpdate !== streamingUpdate;
        const completed = Boolean(metadata.completed_build && previous.completedBuild !== metadata.completed_build);
        const refresh = () => invalidateRenewalConsumers(queryClient, activeAccountId,
            {refetchForecasts: streamed || completed || metadata.forecast_enabled === false});
        if (streamed) {
            // Superseded reads (including an initial load) cannot publish a
            // result from before this streaming notification. One cancellation
            // per observed change; ordinary polling never cancels requests.
            queryClient.cancelQueries({predicate: cached => !!renewalQueryKind(cached.queryKey, activeAccountId)}).then(refresh);
        } else refresh();
    }, [isAuthenticated, options.renewalActive, activeAccountId, version, metadata?.customer_id, queryClient, streamingUpdate, metadata?.completed_build, metadata?.forecast_enabled]);
    const onAvailableAccountsChange = options.onAvailableAccountsChange;
    useEffect(() => {
        // Membership changes must reach the app bar without re-running login or
        // switching accounts. Ignore responses for a different active account.
        if (isAuthenticated && query.isSuccess &&
            query.data?.activeAccount?.customerId === activeAccountId &&
            Array.isArray(query.data?.availableAccounts)) {
            onAvailableAccountsChange?.(query.data.availableAccounts);
        }
    }, [isAuthenticated, activeAccountId, query.isSuccess, query.data, onAvailableAccountsChange]);
    return query;
}
